import { useCallback, useEffect, useState } from "react";
import { getEntitlementsAsync } from "../api/SubscriptionApi";
import { OrgEntitlements } from "../models/SubscriptionModels";
import { Constants } from "../constants/constants";

interface UseOrgEntitlementsReturn {
  entitlements: OrgEntitlements | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export const useOrgEntitlements = (
  organizationId: string | undefined,
  lazy = false,
): UseOrgEntitlementsReturn => {
  const [entitlements, setEntitlements] = useState<OrgEntitlements | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!organizationId) {
      setEntitlements(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
      const res = await getEntitlementsAsync(token, organizationId);
      if (res.success) {
        setEntitlements(res.result ?? null);
        setError(null);
      } else {
        setEntitlements(null);
        setError(res.message || "Failed to load entitlements");
      }
    } catch (err) {
      console.error(err);
      setEntitlements(null);
      setError("Failed to load entitlements");
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (!lazy) load();
  }, [load, lazy]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  return { entitlements, loading, error, reload };
};